import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Printer, X } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

interface ModelResult {
  id: string;
  nome: string;
  marca: string;
  tipoMacchina: string;
  tipoMacchinaLabel: string;
}

interface Props {
  value: string;
  /** Etichetta del modello attualmente selezionato (es. "CANON LBP 6000"). */
  valueLabel: string;
  onChange: (id: string, label: string) => void;
  /** Restringi i risultati a una marca (case-insensitive exact match). */
  marca?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  testid?: string;
  className?: string;
  size?: 'default' | 'sm';
}

/**
 * Combobox asincrono per la ricerca modelli stampante — stesso pattern
 * "live search" della barra di ricerca modelli in navbar (`SearchModelli`):
 * input di testo semplice con dropdown suggerimenti sotto, non un
 * popover/trigger-button. Query debounce 220ms, ricerca server-side su
 * `/api/abbinatore/autocomplete`, trigger dalla 2ª lettera.
 */
export function AsyncModelSearchSelect({
  valueLabel,
  onChange,
  marca,
  placeholder = 'Modello',
  searchPlaceholder,
  emptyMessage = 'Nessun modello trovato',
  disabled,
  testid,
  className = '',
  size = 'default',
}: Props) {
  const [query, setQuery] = useState(valueLabel || '');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState<ModelResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reqIdRef = useRef(0);

  // Sincronizza il testo visibile con il valore esterno quando non si sta
  // attivamente digitando (es. reset dal componente padre).
  useEffect(() => {
    if (!open) setQuery(valueLabel || '');
  }, [valueLabel, open]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (q.length < 2) { setResults([]); setLoading(false); return; }
    setLoading(true);
    const myReqId = ++reqIdRef.current;
    debounceRef.current = setTimeout(() => {
      const params: Record<string, string | number> = { q, limit: 50 };
      if (marca) params.marca = marca;
      axios
        .get<{ results: ModelResult[] }>(`${API}/api/abbinatore/autocomplete`, { params })
        .then(r => {
          if (myReqId !== reqIdRef.current) return; // risposta obsoleta
          const sorted = (r.data.results || []).slice().sort((a, b) =>
            `${a.marca} ${a.nome}`.localeCompare(`${b.marca} ${b.nome}`, 'it')
          );
          setResults(sorted);
          setSelectedIdx(0);
        })
        .catch(() => { if (myReqId === reqIdRef.current) setResults([]); })
        .finally(() => { if (myReqId === reqIdRef.current) setLoading(false); });
    }, 220);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, marca]);

  const handleSelect = (m: ModelResult) => {
    // Mostra solo il nome modello nel campo (senza marca) — la marca è
    // comunque visibile nel filtro "Marca" adiacente (richiesta utente
    // 10/07/2026).
    const label = m.nome.trim();
    onChange(m.id, label);
    setQuery(label);
    setOpen(false);
    setResults([]);
  };

  const handleClear = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    onChange('', '');
    setQuery('');
    setOpen(false);
    setResults([]);
    inputRef.current?.focus();
  };

  // Chiudi dropdown al click esterno
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIdx(i => Math.min(i + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && results[selectedIdx]) { e.preventDefault(); handleSelect(results[selectedIdx]); }
    else if (e.key === 'Escape') { setOpen(false); inputRef.current?.blur(); }
  };

  const isSm = size === 'sm';
  const sizeClasses = isSm ? 'h-9 px-2.5 text-xs gap-1.5' : 'h-11 px-3 text-sm gap-2';
  const showDropdown = open && query.trim().length >= 2;

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div
        className={`flex items-center rounded-lg border bg-white transition-colors ${sizeClasses} ${open ? 'ring-2 ring-[hsl(214,58%,41%)]/20 border-[hsl(214,58%,41%)]' : ''} ${disabled ? 'opacity-60' : ''}`}
      >
        {loading
          ? <Loader2 className={`text-muted-foreground flex-shrink-0 animate-spin ${isSm ? 'w-3 h-3' : 'w-3.5 h-3.5'}`} />
          : <Printer className={`text-muted-foreground flex-shrink-0 ${isSm ? 'w-3 h-3' : 'w-3.5 h-3.5'}`} />
        }
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => { if (!disabled) setOpen(true); }}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder={placeholder}
          data-testid={testid}
          autoComplete="off"
          aria-label={placeholder}
          className="flex-1 h-full min-w-0 bg-transparent outline-none placeholder:text-muted-foreground font-medium disabled:cursor-not-allowed"
        />
        {query && !disabled && (
          <button type="button" onClick={handleClear} tabIndex={-1} className="text-muted-foreground hover:text-foreground flex-shrink-0">
            <X className={isSm ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
          </button>
        )}
      </div>

      {showDropdown && (
        <div
          className="absolute top-full left-0 min-w-[280px] mt-1.5 bg-white border border-gray-200 rounded-xl shadow-2xl z-[200] overflow-hidden"
          data-testid={testid ? `${testid}-dropdown` : undefined}
        >
          {!loading && results.length === 0 ? (
            <div className="px-4 py-4 text-center">
              <p className="text-sm text-muted-foreground">{emptyMessage}</p>
              {searchPlaceholder && <p className="text-[11px] text-muted-foreground mt-1">{searchPlaceholder}</p>}
            </div>
          ) : (
            <div className="max-h-96 overflow-y-auto">
              {results.map((m, idx) => (
                <button
                  key={m.id}
                  type="button"
                  data-testid={testid ? `${testid}-option-${m.id}` : undefined}
                  onClick={() => handleSelect(m)}
                  onMouseEnter={() => setSelectedIdx(idx)}
                  className={`w-full text-left px-4 py-1.5 text-[12px] font-medium transition-colors border-b border-gray-100 last:border-0 ${
                    idx === selectedIdx ? 'bg-[hsl(214,58%,50%)] text-white' : 'text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  {m.marca} – {m.nome}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
